import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const AMBER = [245, 158, 11] as const;
const SLATE_900 = [15, 23, 42] as const;
const GRAY_600 = [75, 85, 99] as const;
const WHITE = [255, 255, 255] as const;
const LIGHT_GRAY = [156, 163, 175] as const;

const PAGE_W = 210;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

function checkPageBreak(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > 275) {
    doc.addPage();
    return 20;
  }
  return y;
}

function addHeader(doc: jsPDF, y: number, text: string): number {
  y = checkPageBreak(doc, y, 14);
  doc.setFillColor(...SLATE_900);
  doc.roundedRect(MARGIN, y, CONTENT_W, 10, 2, 2, 'F');
  doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(...WHITE);
  doc.text(text, MARGIN + 4, y + 7);
  return y + 16;
}

function addBody(doc: jsPDF, y: number, text: string, opts?: { bold?: boolean; italic?: boolean; color?: readonly [number, number, number] }): number {
  y = checkPageBreak(doc, y, 8);
  const style = opts?.bold ? 'bold' : opts?.italic ? 'italic' : 'normal';
  doc.setFont('helvetica', style).setFontSize(10).setTextColor(...(opts?.color ?? GRAY_600));
  const lines = doc.splitTextToSize(text, CONTENT_W);
  doc.text(lines, MARGIN, y);
  return y + lines.length * 5 + 2;
}

function addBullet(doc: jsPDF, y: number, text: string): number {
  y = checkPageBreak(doc, y, 8);
  const x = MARGIN + 4;
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...SLATE_900);
  doc.setFillColor(...AMBER);
  doc.circle(x - 2, y - 1.2, 0.8, 'F');
  doc.setTextColor(...GRAY_600);
  const lines = doc.splitTextToSize(text, CONTENT_W - 8);
  doc.text(lines, x + 2, y);
  return y + lines.length * 5 + 1.5;
}

function addTable(doc: jsPDF, y: number, head: string[][], bodyRows: string[][]): number {
  y = checkPageBreak(doc, y, 20);
  autoTable(doc, {
    startY: y,
    head,
    body: bodyRows,
    margin: { left: MARGIN, right: MARGIN },
    styles: { fontSize: 9, font: 'helvetica', cellPadding: 3 },
    headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    theme: 'grid',
  });
  return (doc as any).lastAutoTable.finalY + 6;
}

function addSeparator(doc: jsPDF, y: number): number {
  y = checkPageBreak(doc, y, 6);
  doc.setDrawColor(209, 213, 219);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  return y + 6;
}

export async function generateMonetizationDoc() {
  const doc = new jsPDF('p', 'mm', 'a4');

  doc.setFillColor(...SLATE_900);
  doc.rect(0, 0, 210, 55, 'F');
  doc.setFillColor(...AMBER);
  doc.rect(0, 55, 210, 3, 'F');

  doc.setFont('helvetica', 'bold').setFontSize(26).setTextColor(...WHITE);
  doc.text('Podium Tournaments', PAGE_W / 2, 22, { align: 'center' });
  doc.setFontSize(16).setTextColor(...LIGHT_GRAY);
  doc.text('Monetization Strategy', PAGE_W / 2, 33, { align: 'center' });
  doc.setFontSize(9);
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    PAGE_W / 2, 45, { align: 'center' }
  );

  let y = 66;

  // Section 1
  y = addHeader(doc, y, '1. Does Charging Teams Annually Make Sense?');
  y = addBody(doc, y, 'Yes, absolutely. An annual team subscription is one of the most natural monetization paths for a platform like Podium, for several key reasons:', { bold: true });
  y = addBullet(doc, y, 'Teams are the sticky unit. A coach or team admin manages a roster, registers for tournaments, communicates with athletes/parents — they use the platform repeatedly and depend on it. That makes them willing to pay for reliability and power features.');
  y = addBullet(doc, y, 'Recurring revenue is predictable. Annual plans give you revenue visibility and reduce churn versus monthly billing.');
  y = addBullet(doc, y, 'It doesn\'t conflict with event revenue. Charging teams for team management features is a separate axis from charging for event hosting/entry fees. You can do both without double-dipping on the same value.');
  y = addBullet(doc, y, 'Competitors do this. Platforms like TeamSnap, SportsEngine, and Trackwrestling all charge teams/clubs on a subscription basis.');
  y += 2;
  y = addBody(doc, y, 'Recommended pricing model: Offer both 6-month and annual options, with annual at a discount ($180/6-mo or $250/yr CAD — save $110 vs two 6-month periods) to incentivize annual commitments.', { bold: true });
  y = addSeparator(doc, y);

  // Section 2
  y = addHeader(doc, y, '2. Free Tier — Get Teams Hooked, Build Network Effects');
  y = addBody(doc, y, 'The free tier should feel complete for a small team. The goal is low friction onboarding to build your team directory and tournament marketplace.');
  y += 2;
  y = addTable(doc, y,
    [['Feature', 'Rationale']],
    [
      ['Team creation + basic profile', 'Low barrier to entry, builds your team directory'],
      ['Basic roster management (up to ~25 athletes)', 'Enough for small clubs to get value'],
      ['Tournament discovery + registration (1 coach)', 'Critical — every team registering grows your event marketplace'],
      ['Basic team feed (announcements only)', 'Gives a taste of communication tools'],
      ['Public team profile page (basic)', 'Free marketing for them, SEO for you'],
      ['Athlete invite links', 'Frictionless onboarding for athletes'],
      ['1 admin/coach seat', 'Enough to manage a small team'],
    ],
  );
  y = addSeparator(doc, y);

  // Section 3
  y = addHeader(doc, y, '3. Pro Tier — ~$20–25/mo or $240/yr per team');
  y = addBody(doc, y, 'The Pro tier unlocks the depth and power of team management. It should feel like "I\'ve outgrown free" not "free is crippled."');
  y += 2;
  y = addTable(doc, y,
    [['Feature', 'Rationale']],
    [
      ['Unlimited roster size', 'Larger clubs/programs need this'],
      ['Multiple staff seats with granular permissions', 'Gate the number of seats or permission granularity'],
      ['Team event management (practices, camps, forms)', 'High-value organizational tooling'],
      ['Fee collection via Stripe Connect', 'Coaches charging for camps/practices is a killer feature'],
      ['Team page builder + blog', 'Custom marketing site is clearly premium'],
      ['Full communication suite (chat, polls, email blasts)', 'Messaging is a big upgrade'],
      ['Media albums + upload links', 'Rich media management'],
      ['Gamification system (points, streaks, leaderboards)', 'Unique differentiator'],
      ['Practice check-in + attendance tracking', 'Operational depth'],
      ['Calendar (team + personal overlay)', 'Essential team coordination'],
      ['Budget/financial tracking', 'Financial management tools'],
      ['Custom registration/application forms', 'Gate the customization'],
      ['Advanced analytics + athlete stats', 'Data-driven coaching'],
      ['Roster share links', 'Tournament preparation'],
      ['File storage', 'Document management'],
    ],
  );
  y = addSeparator(doc, y);

  // Section 4
  y = addHeader(doc, y, '4. Pro Plus / Coach Pro — ~$30–40/mo or $300–400/yr');
  y = addBody(doc, y, 'The top tier targets serious programs that need video, invoicing, and fundraising capabilities.');
  y += 2;
  y = addTable(doc, y,
    [['Feature', 'Rationale']],
    [
      ['Video Vault (upload, annotate, playlists)', 'Very high perceived value'],
      ['Coach fee management + invoicing', 'Directly saves coaches money vs. external invoicing'],
      ['Fundraising campaigns', 'Revenue generation tools'],
      ['Advanced integrations (Podium Labs, etc.)', 'Power user features'],
      ['Priority support / early access', 'Soft perk, low cost to you'],
      ['Unlimited file/media storage', 'Storage costs scale, so gate the ceiling'],
    ],
  );
  y = addSeparator(doc, y);

  // Section 5
  y = addHeader(doc, y, '5. Separate Revenue Stream: Event Hosting');
  y = addBody(doc, y, 'Don\'t bundle this with team subscriptions. Charge event organizers/organizations separately:');
  y = addBullet(doc, y, 'Per-event fee or percentage of entry fees — entry_fee and add_on_pricing on tournaments, plus registration_payments are already built');
  y = addBullet(doc, y, 'Ticketing cut — ticket_types, tickets, ticket_scans are already built');
  y = addBullet(doc, y, 'Premium event features — streaming/overlay tools, volunteer management, advanced bracket analytics, event marketing pages');
  y = addBullet(doc, y, 'League/season management as a premium org feature');
  y += 2;
  y = addBody(doc, y, 'This keeps team subscriptions focused on team management value and event fees focused on event production value — two independent revenue lines.', { italic: true });
  y = addSeparator(doc, y);

  // Section 6
  y = addHeader(doc, y, '6. Strategic Recommendations');
  y = addBody(doc, y, '1. Start with Free + Pro only.', { bold: true });
  y = addBody(doc, y, 'Don\'t overcomplicate with three tiers at launch. Add Pro Plus later when you have enough users asking for video/invoicing features.');
  y += 2;
  y = addBody(doc, y, '2. Keep tournament registration free for all teams.', { bold: true });
  y = addBody(doc, y, 'This is your network effect — every team that registers for a tournament makes your platform more valuable to event hosts. Never gate registration behind a paywall.');
  y += 2;
  y = addBody(doc, y, '3. Gate by value, not by frustration.', { bold: true });
  y = addBody(doc, y, 'Free should feel complete for a small team. Pro should feel like "I\'ve outgrown free" not "free is crippled."');
  y += 2;
  y = addBody(doc, y, '4. Consider a transaction fee model alongside subscriptions.', { bold: true });
  y = addBody(doc, y, 'For Stripe Connect payments (camp fees, event signups), free teams pay a higher platform fee (e.g., 5%) while Pro teams pay a lower one (e.g., 2%). This way even free teams generate revenue for you.');
  y += 2;
  y = addBody(doc, y, '5. Existing infrastructure supports this model.', { bold: true });
  y = addBody(doc, y, 'Your SubscriptionTier types (free, pro, pro_plus, coach_pro) and billing tables (billing_customers, subscriptions, purchases) already support this model. The infrastructure is there — you mainly need to add feature gating logic throughout the app.');
  y = addSeparator(doc, y);

  // Section 7
  y = addHeader(doc, y, '7. Summary');
  y = addBody(doc, y, 'Annual team subscriptions make a lot of sense for Podium Tournaments. You already have both the features and the billing infrastructure to support a compelling Free/Pro split. The key is gating the depth and power of team management features while keeping the network effect drivers (registration, discovery, basic profiles) free.', { bold: true });

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(...AMBER);
    doc.rect(0, 290, 210, 7, 'F');
    doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...WHITE);
    doc.text('Podium Tournaments — Monetization Strategy', PAGE_W / 2, 294.5, { align: 'center' });
    doc.text(`Page ${i} of ${pageCount}`, 195, 294.5, { align: 'right' });
  }

  doc.save('Podium-Monetization-Strategy.pdf');
}
